import { mountScene } from './scene.js';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const CART_KEY = 'hotchows-preview-cart';
const LANG_KEY = 'hotchows-preview-lang';

function $(sel, root = document) {
  return root.querySelector(sel);
}
function $$(sel, root = document) {
  return [...root.querySelectorAll(sel)];
}

function readCart() {
  try {
    const raw = JSON.parse(localStorage.getItem(CART_KEY) || '[]');
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

function writeCart(items) {
  localStorage.setItem(CART_KEY, JSON.stringify(items));
  syncCartCount();
}

function syncCartCount() {
  const count = readCart().reduce((n, item) => n + (item.qty || 1), 0);
  $$('[data-cart-count]').forEach((el) => {
    el.textContent = String(count);
  });
}

async function loadProducts() {
  if (loadProducts.cache) return loadProducts.cache;
  try {
    const res = await fetch('/assets/products.json', { credentials: 'omit' });
    loadProducts.cache = res.ok ? await res.json() : [];
  } catch {
    loadProducts.cache = [];
  }
  return loadProducts.cache;
}

function initScenes() {
  const query = matchMedia('(prefers-reduced-motion: reduce)');
  let paused = false;
  try { paused = localStorage.getItem('hotchows-motion') === 'paused'; } catch {}
  let cleanups = [];
  const apply = () => {
    cleanups.forEach(dispose => dispose()); cleanups = [];
    const off = query.matches || paused;
    document.documentElement.dataset.motion = off ? 'paused' : 'active';
    $$('[data-motion-toggle]').forEach(button => {
      button.hidden = false;
      button.textContent = query.matches ? 'Reduced motion on' : paused ? 'Enable motion' : 'Pause motion';
      button.disabled = query.matches;
      button.setAttribute('aria-pressed', String(off));
    });
    if (off) return;
    $$('.scene').forEach(host => {
      try { const dispose = mountScene(host); if (dispose) cleanups.push(dispose); }
      catch { host.dataset.fallback = 'scene-error'; }
    });
    $$('.workflow-diagram').forEach(panel => {
      const context = gsap.context(() => {
        gsap.fromTo(panel, { rotationY: -9, rotationX: 5, y: 22 }, {
          rotationY: 5, rotationX: -2, y: -8, ease: 'none',
          scrollTrigger: { trigger: panel.closest('section'), start: 'top bottom', end: 'bottom top', scrub: .6 },
        });
        gsap.fromTo(panel.querySelectorAll('.screen-chart i'), { scaleY: .65, transformOrigin: 'bottom' }, {
          scaleY: 1, stagger: .05, ease: 'none',
          scrollTrigger: { trigger: panel, start: 'top 95%', end: 'center 55%', scrub: .4 },
        });
      }, panel);
      cleanups.push(() => context.revert());
    });

    // Bento 3D Spotlight & Tilt Physics on #products .card, .apps-grid .card, .app-card, .step-hud-card
    const bentoCards = $$('#products .card, .apps-grid .card, .app-card, .step-hud-card');
    const isFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    bentoCards.forEach(card => {
      let rafId = null;
      let targetRotX = 0;
      let targetRotY = 0;
      let currentRotX = 0;
      let currentRotY = 0;
      let isHovering = false;

      const onPointerMove = (e) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        card.style.setProperty('--spotlight-x', `${x}px`);
        card.style.setProperty('--spotlight-y', `${y}px`);
        card.style.setProperty('--spotlight-opacity', '1');

        if (!isFinePointer) return;
        const normX = (x / rect.width - 0.5) * 2;
        const normY = (y / rect.height - 0.5) * 2;
        targetRotX = -normY * 5.5;
        targetRotY = normX * 5.5;

        if (!isHovering) {
          isHovering = true;
          animate();
        }
      };

      const animate = () => {
        if (!isHovering && Math.abs(targetRotX - currentRotX) < 0.05 && Math.abs(targetRotY - currentRotY) < 0.05) {
          card.style.transform = '';
          currentRotX = 0;
          currentRotY = 0;
          rafId = null;
          return;
        }
        currentRotX += (targetRotX - currentRotX) * 0.16;
        currentRotY += (targetRotY - currentRotY) * 0.16;
        card.style.transform = `perspective(1000px) rotateX(${currentRotX.toFixed(2)}deg) rotateY(${currentRotY.toFixed(2)}deg) scale3d(1.018, 1.018, 1.018)`;
        rafId = requestAnimationFrame(animate);
      };

      const onPointerLeave = () => {
        card.style.setProperty('--spotlight-opacity', '0');
        targetRotX = 0;
        targetRotY = 0;
        isHovering = false;
        if (!rafId) animate();
      };

      card.addEventListener('pointermove', onPointerMove);
      card.addEventListener('pointerleave', onPointerLeave);

      cleanups.push(() => {
        card.removeEventListener('pointermove', onPointerMove);
        card.removeEventListener('pointerleave', onPointerLeave);
        if (rafId) cancelAnimationFrame(rafId);
        card.style.transform = '';
        card.style.removeProperty('--spotlight-x');
        card.style.removeProperty('--spotlight-y');
        card.style.removeProperty('--spotlight-opacity');
      });
    });

    // Micro-stagger viewport entrances with GSAP ScrollTrigger
    const entranceCtx = gsap.context(() => {
      const pCards = $$('#products .card');
      if (pCards.length) {
        gsap.from(pCards, {
          y: 28,
          opacity: 0,
          duration: 0.42,
          stagger: 0.04,
          ease: 'power2.out',
          clearProps: 'transform',
          scrollTrigger: {
            trigger: '#products',
            start: 'top 82%',
            once: true,
          },
        });
      }
      const cCards = $$('#compare .card');
      if (cCards.length) {
        gsap.from(cCards, {
          y: 24,
          opacity: 0,
          duration: 0.42,
          stagger: 0.05,
          ease: 'power2.out',
          clearProps: 'transform',
          scrollTrigger: {
            trigger: '#compare',
            start: 'top 82%',
            once: true,
          },
        });
      }
      const aCards = $$('.apps-grid .card, .app-card');
      if (aCards.length) {
        gsap.from(aCards, {
          y: 28,
          opacity: 0,
          duration: 0.45,
          stagger: 0.06,
          ease: 'power2.out',
          clearProps: 'transform',
          scrollTrigger: {
            trigger: '.apps-grid',
            start: 'top 85%',
            once: true,
          },
        });
      }
      const consoles = $$('.showcase-console');
      if (consoles.length) {
        gsap.from(consoles, {
          y: 30,
          opacity: 0,
          duration: 0.48,
          ease: 'power2.out',
          clearProps: 'transform',
          scrollTrigger: {
            trigger: consoles[0],
            start: 'top 85%',
            once: true,
          },
        });
      }
    });
    cleanups.push(() => entranceCtx.revert());

    ScrollTrigger.refresh();
  };
  $$('[data-motion-toggle]').forEach(button => button.addEventListener('click', () => {
    paused = !paused;
    try { localStorage.setItem('hotchows-motion', paused ? 'paused' : 'active'); } catch {}
    apply();
  }));
  query.addEventListener('change', apply);
  window.addEventListener('pageshow', event => { if (event.persisted) apply(); });
  apply();
}

function initMenu() {
  const header = $('.site-header');
  const toggle = $('.menu-toggle');
  const nav = $('#global-nav');
  if (!header || !toggle || !nav) return;
  const resizeHeader = () => {
    if (!header.classList.contains('menu-open')) {
      document.documentElement.style.setProperty('--nav', `${header.getBoundingClientRect().height}px`);
    }
  };
  new ResizeObserver(resizeHeader).observe(header);
  resizeHeader();
  const setOpen = (open) => {
    header.classList.toggle('menu-open', open);
    toggle.setAttribute('aria-expanded', String(open));
  };
  toggle.addEventListener('click', () => {
    setOpen(!header.classList.contains('menu-open'));
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && header.classList.contains('menu-open')) {
      setOpen(false); toggle.focus();
    }
  });
  nav.addEventListener('click', (event) => {
    if (event.target.closest('a') && header.classList.contains('menu-open')) {
      setOpen(false);
    }
  });
}

function initChat() {
  const launcher = $('.chat-launcher');
  const panel = $('#chat-panel');
  const close = $('.chat-close');
  if (!launcher || !panel) return;
  const setOpen = (open) => {
    panel.hidden = !open;
    launcher.setAttribute('aria-expanded', String(open));
  };
  setOpen(false);
  launcher.addEventListener('click', () => setOpen(panel.hidden));
  close?.addEventListener('click', () => setOpen(false));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') setOpen(false);
  });
  const answers = {
    features:
      'Hotchows is point of sale, inventory, bookkeeping and online ordering for restaurant, retail and service businesses. Marketplace is general merchandise for business buyers and individual shoppers.',
    support:
      'A human is available at mail@hotchows.com or +1 (650) 800-8119. This preview does not send messages to an AI service.',
  };
  $$('[data-help]', panel).forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = $('.chat-answer', panel);
      if (target) target.textContent = answers[btn.dataset.help] || '';
    });
  });
}

function validateForm(form) {
  const status = $('[data-form-status]', form);
  const result = $('[data-auth-result]', form);
  if (status) status.textContent = '';
  if (result) {
    result.hidden = true;
    result.textContent = '';
  }
  if (!form.checkValidity()) {
    form.reportValidity();
    if (status) status.textContent = 'Please complete the required fields. Nothing was sent.';
    return false;
  }
  const password = form.querySelector('#password');
  if (form.dataset.form === 'register' && password && password.value.length < 6) {
    if (status) status.textContent = 'Password must be at least 6 characters. Nothing was sent.';
    return false;
  }
  return true;
}

function initForms() {
  $$('form[data-form]').forEach((form) => {
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!validateForm(form)) return;
      const kind = form.dataset.form;
      const status = $('[data-form-status]', form);
      const result = $('[data-auth-result]', form);
      const message =
        kind === 'contact'
          ? 'Preview only. No message was sent.'
          : kind === 'recovery'
            ? 'Preview only. No recovery email was sent.'
            : kind === 'register'
              ? 'Preview only. No account was created and no credentials were transmitted.'
              : 'Preview only. Sign-in was not submitted. No credentials were transmitted.';
      if (result) {
        result.hidden = false;
        result.textContent = message;
      }
      if (status) status.textContent = message;
    });
  });
  $$('[data-password]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const input = btn.parentElement?.querySelector('input');
      if (!input) return;
      const show = input.type === 'password';
      input.type = show ? 'text' : 'password';
      btn.textContent = show ? 'Hide' : 'Show';
      btn.setAttribute('aria-label', show ? 'Hide password' : 'Show password');
    });
  });
}

function initLanguage() {
  const select = $('select.language');
  const status = $('.lang-status');
  if (!select) return;
  const stored = localStorage.getItem(LANG_KEY);
  if (stored && [...select.options].some((opt) => opt.value === stored)) {
    select.value = stored;
  }
  const announce = () => {
    if (!status) return;
    if (select.value === 'en') {
      status.textContent = '';
      return;
    }
    status.textContent =
      'This private preview is English-only. The language control is preserved; copy is not translated here.';
  };
  announce();
  select.addEventListener('change', () => {
    localStorage.setItem(LANG_KEY, select.value);
    announce();
  });
}

function initScrollMeter() {
  const meter = $('.scroll-meter');
  if (!meter) return;
  const update = () => {
    const max = document.documentElement.scrollHeight - innerHeight;
    meter.style.width = `${max > 0 ? (scrollY / max) * 100 : 0}%`;
  };
  addEventListener('scroll', update, { passive: true });
  update();
}

function initCatalog() {
  const grid = $('#catalog-grid');
  if (!grid) return;
  const search = $('#product-search');
  const filter = $('#category-filter');
  const count = $('#product-count');
  const empty = $('#no-results');
  const params = new URLSearchParams(location.search);
  if (search && params.get('q') && !search.value) search.value = params.get('q');
  if (filter && params.get('categoryId')) filter.value = params.get('categoryId');
  const apply = () => {
    const q = (search?.value || '').trim().toLowerCase();
    const category = filter?.value || '';
    let visible = 0;
    $$('.product', grid).forEach((card) => {
      const hay = card.dataset.search || '';
      const matchQ = !q || hay.includes(q);
      const matchC = !category || card.dataset.category === category;
      const show = matchQ && matchC;
      card.hidden = !show;
      if (show) visible += 1;
    });
    if (count) count.textContent = `${visible} preview listing${visible === 1 ? '' : 's'}`;
    if (empty) empty.hidden = visible !== 0;
  };
  search?.addEventListener('input', apply);
  filter?.addEventListener('change', apply);
  const slug = params.get('category');
  if (slug && !filter?.value) {
    const status = $('#category-status');
    if (status) {
      status.textContent = `Category filter from URL: ${slug}. Matching is limited to this captured snapshot.`;
    }
  }
  apply();
}

function initShops() {
  const search = $('#shop-search');
  if (!search) return;
  const count = $('#shop-count');
  const apply = () => {
    const q = search.value.trim().toLowerCase();
    let visible = 0;
    $$('[data-shop]').forEach((card) => {
      const show = !q || (card.dataset.shop || '').includes(q);
      card.hidden = !show;
      if (show) visible += 1;
    });
    if (count) count.textContent = `${visible} shop${visible === 1 ? '' : 's'} in this preview`;
  };
  search.addEventListener('input', apply);
  apply();
}

function initShowcase() {
  $$('[data-showcase]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const result = $('[data-showcase-result]') || btn.parentElement?.querySelector('[data-showcase-result]');
      if (result) {
        result.textContent =
          'Sample review state only. No scan, research job or external request was started.';
      }
    });
  });
}

function initAppSimulators() {
  // 1. ADA Compliance Inspector Simulator
  $$('[data-ada-inspector]').forEach((inspector) => {
    const runBtn = $('[data-action="run-ada-scan"]', inspector);
    const resetBtn = $('[data-action="reset-ada-scan"]', inspector);
    const scoreVal = $('[data-ada-score]', inspector);
    const scoreBar = $('[data-ada-score-bar]', inspector);
    const contrastVal = $('[data-ada-contrast]', inspector);
    const issuesVal = $('[data-ada-issues]', inspector);
    const feedback = $('[data-ada-feedback]', inspector);
    const diffAdd = $('[data-ada-diff-add]', inspector);

    if (runBtn) {
      runBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (feedback) feedback.textContent = 'Scanning 1,842 DOM nodes & AST accessibility tree...';
        runBtn.disabled = true;
        setTimeout(() => {
          if (scoreVal) scoreVal.textContent = '100%';
          if (scoreBar) scoreBar.style.width = '100%';
          if (contrastVal) contrastVal.textContent = '7.2:1 AAA PASS';
          if (issuesVal) issuesVal.textContent = '0';
          if (diffAdd) diffAdd.classList.add('pulse-diff');
          if (feedback) feedback.textContent = 'Synthetic scan completed: 1 advisory resolved at source level. ARIA landmarks verified. 0 violations.';
          runBtn.hidden = true;
          runBtn.disabled = false;
          if (resetBtn) resetBtn.hidden = false;
        }, 350);
      });
    }

    if (resetBtn) {
      resetBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (scoreVal) scoreVal.textContent = '98%';
        if (scoreBar) scoreBar.style.width = '98%';
        if (contrastVal) contrastVal.textContent = '7.2:1';
        if (issuesVal) issuesVal.textContent = '0';
        if (diffAdd) diffAdd.classList.remove('pulse-diff');
        if (feedback) feedback.textContent = 'Synthetic inspector reset. Ready to inspect AST nodes.';
        resetBtn.hidden = true;
        if (runBtn) {
          runBtn.hidden = false;
          runBtn.disabled = false;
        }
      });
    }
  });

  // 2. Deep Market Intel Simulator
  $$('[data-market-inspector]').forEach((inspector) => {
    const sourcePills = $$('[data-source-id]', inspector);
    const headline = $('[data-market-headline]', inspector);
    const snippet = $('[data-market-snippet]', inspector);
    const feedback = $('[data-market-feedback]', inspector);
    const traverseBtn = $('[data-action="inspect-market-node"]', inspector);

    const sources = {
      sec: {
        headline: 'SEC 10-K Filings: Multi-Unit Software Margin Deconstruction',
        snippet: 'Cross-referenced 4 primary enterprise filings. Gross software margins expanded from 71.4% to 78.2% across cloud-native restaurant operators transitioning to integrated bookkeeping workflows.',
        feedback: 'Loaded SEC 10-K primary filing record. Confidence: 96%. All claims grounded.'
      },
      patents: {
        headline: 'US Patent Gazette: Autonomous Order Routing & Edge Telemetry',
        snippet: 'Patent #2026/019 grants real-time offline synchronization and sub-10ms payment tokenization for high-throughput merchants.',
        feedback: 'Loaded Patent Gazette citation. Confidence: 98%. Autonomous edge protocols verified.'
      },
      earnings: {
        headline: 'Q3 Earnings Transcripts: Executive Commentary on Platform Retention',
        snippet: 'Synthesized commentary across 12 earnings calls. Operators cite single-pane-of-glass POS + accounting as primary churn-reduction factor (+34% LTV).',
        feedback: 'Loaded Q3 executive earnings transcripts. Confidence: 92%. Cross-validated against transcripts.'
      },
      pricing: {
        headline: 'Interchange Schedule Regs: Transparent Fee Unbundling Trends',
        snippet: 'Regulatory disclosure analysis: Merchants adopting decoupled software-first POS retain freedom of payment processor with 0 vendor lock-in.',
        feedback: 'Loaded Interchange Regulatory schedules. Confidence: 95%. Decoupled fee rules grounded.'
      }
    };

    sourcePills.forEach((pill) => {
      pill.addEventListener('click', (e) => {
        e.preventDefault();
        sourcePills.forEach((p) => {
          p.classList.remove('active');
          p.setAttribute('aria-selected', 'false');
        });
        pill.classList.add('active');
        pill.setAttribute('aria-selected', 'true');
        const id = pill.dataset.sourceId;
        const data = sources[id];
        if (data) {
          if (headline) headline.textContent = data.headline;
          if (snippet) snippet.textContent = data.snippet;
          if (feedback) feedback.textContent = data.feedback;
        }
      });
    });

    if (traverseBtn) {
      let step = 0;
      traverseBtn.addEventListener('click', (e) => {
        e.preventDefault();
        step = (step + 1) % sourcePills.length;
        sourcePills[step]?.click();
        if (feedback) feedback.textContent = `Graph traversal step ${step + 1} of ${sourcePills.length}: Verified interconnected citation cluster.`;
      });
    }
  });

  // 3. B2B Account Intel Simulator
  $$('[data-b2b-inspector]').forEach((inspector) => {
    const layerPills = $$('[data-layer-id]', inspector);
    const panel = $('[data-b2b-panel]', inspector);
    const feedback = $('[data-b2b-feedback]', inspector);
    const toggleBtn = $('[data-action="toggle-dossier-layer"]', inspector);

    const layers = {
      org: {
        html: `<div class="org-tree-grid">
          <div class="org-node">
            <span class="node-role">VP Infrastructure</span>
            <span class="node-name">Marcus Vance</span>
            <small class="node-status">Direct Sponsor · Evaluated POS API</small>
          </div>
          <div class="org-node">
            <span class="node-role">Director of Store Ops</span>
            <span class="node-name">Elena Rostova</span>
            <small class="node-status">End-User Stakeholder · 42 Locations</small>
          </div>
          <div class="org-node">
            <span class="node-role">Head of Procurement</span>
            <span class="node-name">David Chen</span>
            <small class="node-status">Contract Gate · Q4 Renewal</small>
          </div>
        </div>`,
        feedback: 'Showing Layer 1: Organizational Hierarchy & Verified Stakeholder Telemetry.'
      },
      tech: {
        html: `<div class="tech-stack-grid">
          <div class="tech-badge"><span class="tech-dot dot-aws"></span>AWS Lambda &amp; Fargate</div>
          <div class="tech-badge"><span class="tech-dot dot-pg"></span>PostgreSQL High-Availability</div>
          <div class="tech-badge"><span class="tech-dot dot-kafka"></span>Apache Kafka Event Bus</div>
          <div class="tech-badge"><span class="tech-dot dot-stripe"></span>Stripe Billing Infrastructure</div>
          <div class="tech-badge"><span class="tech-dot dot-next"></span>Next.js / Node 22 Edge UI</div>
        </div>`,
        feedback: 'Showing Layer 2: Detected Technology Infrastructure & Vendor Signals.'
      },
      intent: {
        html: `<div class="intent-signals-list">
          <div class="intent-card">
            <div class="intent-header"><b>FACILITY EXPANSION</b><span class="val-emerald">+42% INTENT</span></div>
            <p>New 48,000 sq ft Logistics Hub permitting filed in Q2. Active demand for synchronized inventory endpoints.</p>
          </div>
          <div class="intent-card">
            <div class="intent-header"><b>ERP MIGRATION RFP</b><span class="val-amber">ACTIVE WINDOW</span></div>
            <p>Legacy POS contract renewal identified for October 2026. Evaluating decoupled commerce architectures.</p>
          </div>
        </div>`,
        feedback: 'Showing Layer 3: Growth Triggers & High-Intent Purchase Signals.'
      }
    };

    layerPills.forEach((pill) => {
      pill.addEventListener('click', (e) => {
        e.preventDefault();
        layerPills.forEach((p) => {
          p.classList.remove('active');
          p.setAttribute('aria-selected', 'false');
        });
        pill.classList.add('active');
        pill.setAttribute('aria-selected', 'true');
        const id = pill.dataset.layerId;
        const data = layers[id];
        if (data) {
          if (panel) panel.innerHTML = data.html;
          if (feedback) feedback.textContent = data.feedback;
        }
      });
    });

    if (toggleBtn) {
      let current = 0;
      toggleBtn.addEventListener('click', (e) => {
        e.preventDefault();
        current = (current + 1) % layerPills.length;
        layerPills[current]?.click();
      });
    }
  });

  // 4. B2B Lead Enrichment Simulator
  $$('[data-lead-inspector]').forEach((inspector) => {
    const cycleBtn = $('[data-action="inspect-lead-domain"]', inspector);
    const targetUrl = $('[data-lead-target-url]', inspector);
    const panel = $('[data-lead-panel]', inspector);
    const feedback = $('[data-lead-feedback]', inspector);
    const confVal = $('[data-lead-conf]', inspector);
    const mxVal = $('[data-lead-mx]', inspector);
    const tabs = $$('[data-lead-tab]', inspector);

    const companies = [
      {
        domain: 'apexlogistics.io',
        url: 'apexlogistics.io/decision-makers',
        conf: '96% Pattern Fit',
        mx: 'Google Workspace',
        feedback: 'Autonomous OSINT active: Discovered 3 decision-makers at Apex Logistics ({first}.{last} verified).',
        contactsHtml: `<div class="lead-contacts-table">
          <div class="lead-contact-row">
            <div class="contact-info"><span class="contact-name">Marcus Vance</span><span class="contact-role">VP Infrastructure &amp; Cloud</span></div>
            <div class="contact-email"><code>marcus.vance@apexlogistics.io</code><span class="status-pill status-active">PATTERN: {first}.{last}</span></div>
            <div class="contact-status"><span class="metric-tag tag-pass">MX DELIVERABLE</span></div>
          </div>
          <div class="lead-contact-row">
            <div class="contact-info"><span class="contact-name">Elena Rostova</span><span class="contact-role">Director of Store Operations</span></div>
            <div class="contact-email"><code>elena.rostova@apexlogistics.io</code><span class="status-pill status-active">PATTERN: {first}.{last}</span></div>
            <div class="contact-status"><span class="metric-tag tag-pass">MX DELIVERABLE</span></div>
          </div>
          <div class="lead-contact-row">
            <div class="contact-info"><span class="contact-name">David Chen</span><span class="contact-role">Head of Procurement</span></div>
            <div class="contact-email"><code>david.chen@apexlogistics.io</code><span class="status-pill status-active">PATTERN: {first}.{last}</span></div>
            <div class="contact-status"><span class="metric-tag tag-pass">MX DELIVERABLE</span></div>
          </div>
        </div>`
      },
      {
        domain: 'cloudscalelabs.tech',
        url: 'cloudscalelabs.tech/engineering-leads',
        conf: '98% Pattern Fit',
        mx: 'Microsoft 365',
        feedback: 'Discovered CloudScale Labs targets: CTO and VP Platform ({f}{last} verified with M365 MX).',
        contactsHtml: `<div class="lead-contacts-table">
          <div class="lead-contact-row">
            <div class="contact-info"><span class="contact-name">Aria Thorne</span><span class="contact-role">Chief Technology Officer</span></div>
            <div class="contact-email"><code>athorne@cloudscalelabs.tech</code><span class="status-pill status-active">PATTERN: {f}{last}</span></div>
            <div class="contact-status"><span class="metric-tag tag-pass">MX DELIVERABLE</span></div>
          </div>
          <div class="lead-contact-row">
            <div class="contact-info"><span class="contact-name">Kiran Patel</span><span class="contact-role">VP Platform Engineering</span></div>
            <div class="contact-email"><code>kpatel@cloudscalelabs.tech</code><span class="status-pill status-active">PATTERN: {f}{last}</span></div>
            <div class="contact-status"><span class="metric-tag tag-pass">MX DELIVERABLE</span></div>
          </div>
        </div>`
      }
    ];

    let currentIdx = 0;
    if (cycleBtn) {
      cycleBtn.addEventListener('click', (e) => {
        e.preventDefault();
        currentIdx = (currentIdx + 1) % companies.length;
        const item = companies[currentIdx];
        if (targetUrl) targetUrl.textContent = item.url;
        if (panel) panel.innerHTML = item.contactsHtml;
        if (confVal) confVal.textContent = item.conf;
        if (mxVal) mxVal.textContent = item.mx;
        if (feedback) feedback.textContent = item.feedback;
      });
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        tabs.forEach((t) => {
          t.classList.remove('active');
          t.setAttribute('aria-selected', 'false');
        });
        tab.classList.add('active');
        tab.setAttribute('aria-selected', 'true');
        const view = tab.dataset.leadTab;
        if (view === 'mx') {
          if (panel) panel.innerHTML = `<div class="mx-check-console">
            <div class="mx-check-row"><b>DNS HOST:</b> <code>aspmx.l.google.com (Priority 1)</code> <span class="metric-tag tag-pass">VALID</span></div>
            <div class="mx-check-row"><b>ALT HOST:</b> <code>alt1.aspmx.l.google.com (Priority 5)</code> <span class="metric-tag tag-pass">VALID</span></div>
            <div class="mx-check-row"><b>SPF RECORD:</b> <code>v=spf1 include:_spf.google.com ~all</code> <span class="metric-tag tag-pass">PASS</span></div>
          </div>`;
          if (feedback) feedback.textContent = 'Inspecting live DNS MX routing. Zero bounce deliverability confirmed.';
        } else if (view === 'crm') {
          if (panel) panel.innerHTML = `<pre class="json-crm-viewer"><code>{
  "company": "Apex Logistics & Retail",
  "domain": "apexlogistics.io",
  "pattern": "{first}.{last}",
  "decision_makers": [
    {"name": "Marcus Vance", "role": "VP Infra", "email": "marcus.vance@apexlogistics.io", "verified": true},
    {"name": "Elena Rostova", "role": "Dir Ops", "email": "elena.rostova@apexlogistics.io", "verified": true}
  ]
}</code></pre>`;
          if (feedback) feedback.textContent = 'Structured CRM JSON ready for HubSpot, Salesforce, or webhook ingestion.';
        } else {
          if (panel) panel.innerHTML = companies[currentIdx].contactsHtml;
          if (feedback) feedback.textContent = companies[currentIdx].feedback;
        }
      });
    });
  });

  // 5. Viral Video Studio Simulator
  $$('[data-video-inspector]').forEach((inspector) => {
    const cycleBtn = $('[data-action="toggle-video-remix"]', inspector);
    const modeTabs = $$('[data-video-mode]', inspector);
    const desc = $('[data-video-desc]', inspector);
    const feedback = $('[data-video-feedback]', inspector);
    const timecode = $('[data-video-timecode]', inspector);

    const modes = {
      product: {
        desc: '<b>SCENE 01 / ANCHOR FRAME:</b><br>Hand picks up Cold Brew Nitro Can from stainless counter. Kinetic camera zoom with dynamic motion blur.',
        feedback: 'Mode: Product Swap. Preserves camera motion and lighting while re-generating anchor product frame.',
        time: '00:04.2 / 00:06.0'
      },
      mood: {
        desc: '<b>SCENE 01 / ANCHOR FRAME:</b><br>Cyberpunk neon night ambiance: reflective rain on counter, anamorphic cyan-magenta lens flare.',
        feedback: 'Mode: Lighting & Mood Remix. Restyles palette and lighting while preserving subject geometry.',
        time: '00:05.1 / 00:06.0'
      },
      hook: {
        desc: '<b>SCENE 01 / ANCHOR FRAME:</b><br>Bold kinetic typography overlay: "NEVER BUY COLD BREW AGAIN". Shake-transition intro.',
        feedback: 'Mode: Hook Headline. Injects high pattern-interrupt typography in the first 1.5 seconds.',
        time: '00:01.8 / 00:06.0'
      }
    };

    const keys = Object.keys(modes);
    let keyIdx = 0;

    modeTabs.forEach((tab) => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        modeTabs.forEach((t) => {
          t.classList.remove('active');
          t.setAttribute('aria-selected', 'false');
        });
        tab.classList.add('active');
        tab.setAttribute('aria-selected', 'true');
        const m = tab.dataset.videoMode;
        if (modes[m]) {
          if (desc) desc.innerHTML = modes[m].desc;
          if (feedback) feedback.textContent = modes[m].feedback;
          if (timecode) timecode.textContent = modes[m].time;
        }
      });
    });

    if (cycleBtn) {
      cycleBtn.addEventListener('click', (e) => {
        e.preventDefault();
        keyIdx = (keyIdx + 1) % keys.length;
        modeTabs[keyIdx]?.click();
      });
    }
  });

  // 6. Hook Lens Simulator
  $$('[data-hook-inspector]').forEach((inspector) => {
    const cycleBtn = $('[data-action="cycle-hook-archetype"]', inspector);
    const tabs = $$('[data-hook-archetype]', inspector);
    const score = $('[data-hook-score]', inspector);
    const feedback = $('[data-hook-feedback]', inspector);
    const rewrite = $('[data-hook-rewrite]', inspector);
    const range = $('[data-hook-range]', inspector);
    const patVal = $('[data-rubric-pattern]', inspector);
    const clarVal = $('[data-rubric-clarity]', inspector);

    const archetypes = {
      negative: {
        score: 'SCORE: 91 / 100',
        range: '74% – 82% Retained',
        pat: '9.4 / 10',
        clar: '9.1 / 10',
        feedback: 'Analyzed Negative Constraint opening. High pattern interrupt (+28% projected retention over benchmark).',
        rewriteHtml: `<div class="rewrite-col weak">
          <span class="rewrite-badge">ORIGINAL WEAK OPENING (38% RETENTION)</span>
          <p>"Hey guys, today I want to share a few tips about managing your restaurant point of sale..."</p>
        </div>
        <div class="rewrite-col strong">
          <span class="rewrite-badge badge-pass">HOOK LENS PRESCRIPTION (78% RETENTION)</span>
          <p>"Stop closing your register like this: this 1 button error costs multi-unit operators $14,000 every Saturday."</p>
        </div>`
      },
      curiosity: {
        score: 'SCORE: 88 / 100',
        range: '70% – 79% Retained',
        pat: '8.9 / 10',
        clar: '9.3 / 10',
        feedback: 'Analyzed Curiosity Gap opening. Strong payoff promise with low cognitive friction.',
        rewriteHtml: `<div class="rewrite-col weak">
          <span class="rewrite-badge">ORIGINAL WEAK OPENING (41% RETENTION)</span>
          <p>"Here is an interesting story about how we saved money on interchange rates."</p>
        </div>
        <div class="rewrite-col strong">
          <span class="rewrite-badge badge-pass">HOOK LENS PRESCRIPTION (75% RETENTION)</span>
          <p>"There is a secret fee hidden inside every swipe statement that banks don't legally have to explain."</p>
        </div>`
      },
      demo: {
        score: 'SCORE: 94 / 100',
        range: '80% – 88% Retained',
        pat: '9.7 / 10',
        clar: '9.5 / 10',
        feedback: 'Analyzed Immediate Demonstration hook. Maximum pattern interrupt in first 0.8 seconds.',
        rewriteHtml: `<div class="rewrite-col weak">
          <span class="rewrite-badge">ORIGINAL WEAK OPENING (45% RETENTION)</span>
          <p>"We tested whether thermal printers survive kitchen heat."</p>
        </div>
        <div class="rewrite-col strong">
          <span class="rewrite-badge badge-pass">HOOK LENS PRESCRIPTION (84% RETENTION)</span>
          <p>"We put a 400-degree pizza paddle directly on our touch terminal to see what melts first."</p>
        </div>`
      }
    };

    const archKeys = Object.keys(archetypes);
    let archIdx = 0;

    tabs.forEach((tab) => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        tabs.forEach((t) => {
          t.classList.remove('active');
          t.setAttribute('aria-selected', 'false');
        });
        tab.classList.add('active');
        tab.setAttribute('aria-selected', 'true');
        const a = tab.dataset.hookArchetype;
        const item = archetypes[a];
        if (item) {
          if (score) score.textContent = item.score;
          if (range) range.textContent = item.range;
          if (patVal) patVal.textContent = item.pat;
          if (clarVal) clarVal.textContent = item.clar;
          if (rewrite) rewrite.innerHTML = item.rewriteHtml;
          if (feedback) feedback.textContent = item.feedback;
        }
      });
    });

    if (cycleBtn) {
      cycleBtn.addEventListener('click', (e) => {
        e.preventDefault();
        archIdx = (archIdx + 1) % archKeys.length;
        tabs[archIdx]?.click();
      });
    }
  });
}

async function addToCart(id) {
  const products = await loadProducts();
  const product = products.find((item) => item.id === id);
  if (!product) return;
  const items = readCart();
  const existing = items.find((item) => item.id === id);
  if (existing) existing.qty += 1;
  else items.push({ id, qty: 1 });
  writeCart(items);
  $$('[data-cart-status]').forEach((el) => {
    el.textContent = `${product.name} added to the local preview cart. Checkout is disabled.`;
  });
  if (location.pathname.replace(/\.html$/, '') === '/marketplace/cart') renderCart(products);
}

async function renderCart(products) {
  const root = $('#cart-items');
  const status = $('#cart-status');
  if (!root) return;
  const items = readCart();
  if (!items.length) {
    root.innerHTML = '';
    if (status) status.textContent = 'Preview cart is empty. Nothing will be ordered.';
    return;
  }
  root.innerHTML = items
    .map((item) => {
      const product = products.find((row) => row.id === item.id);
      if (!product) return '';
      const photo = product.photo
        ? `<img src="${product.photo}" alt="" width="90" height="80">`
        : '';
      return `<article class="cart-row" data-cart-id="${product.id}">${photo}<div><h2>${product.name}</h2><p class="amount">$${product.price} USD · qty ${item.qty}</p></div><button type="button" data-cart-remove="${product.id}">Remove</button></article>`;
    })
    .join('');
  if (status) status.textContent = `${items.length} local preview item${items.length === 1 ? '' : 's'}. Checkout is unavailable.`;
}

function initCart() {
  syncCartCount();
  document.addEventListener('click', async (event) => {
    const add = event.target.closest('[data-cart-add]');
    if (add) {
      event.preventDefault();
      await addToCart(add.getAttribute('data-cart-add'));
      return;
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      writeCart(readCart().filter((item) => item.id !== remove.getAttribute('data-cart-remove')));
      renderCart(await loadProducts());
    }
  });
  if ($('#cart-items')) loadProducts().then(renderCart);
}

function initScrollableRegions() {
  const focusable = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])';
  $$('body *').forEach((el) => {
    if (el.closest('[data-scroll-fixed]')) return;
    const cs = getComputedStyle(el);
    const scrollable = ['auto', 'scroll'].includes(cs.overflowY) || ['auto', 'scroll'].includes(cs.overflow);
    if (!scrollable) return;
    if (el.scrollHeight <= el.clientHeight + 4 && el.scrollWidth <= el.clientWidth + 4) return;
    if (el.hasAttribute('tabindex')) return;
    const hasFocusableChild = Boolean(el.querySelector(focusable));
    const named = el.hasAttribute('aria-label') || el.hasAttribute('aria-labelledby') || el.querySelector('caption');
    if (!hasFocusableChild && !named) return;
    el.setAttribute('tabindex', '0');
    el.setAttribute('data-scroll-fixed', '');
    if (!named) el.setAttribute('aria-label', 'Scrollable section. Use arrow keys after focusing.');
  });
}

function init() {
  initScenes();
  initMenu();
  initChat();
  initForms();
  initLanguage();
  initScrollMeter();
  initCatalog();
  initShops();
  initShowcase();
  initAppSimulators();
  initCart();
  initScrollableRegions();
}

init();
